import React, { useEffect, useRef } from "react";
import { pickColor, rgba } from "./internal/colorUtils";

export const key = "atom-orbits";

const DEFAULT_BG = "#0a0e1f";
const DEFAULT_NUCLEUS = "#fde68a";
const DEFAULT_ORBIT = "#64748b";
const DEFAULT_ELECTRON = "#22d3ee";

// Five orbits, each living on its own tilted plane in 3D, sharing a
// single nucleus at the origin. Each orbit has a rotation matrix that
// takes points on the canonical XY-plane ellipse and rotates them onto
// the orbit's plane. We sample the ellipse densely for the visible orbit
// path, and parameterize one electron per orbit moving at its own angular
// speed. Everything is depth-sorted before drawing so electrons correctly
// pass behind the nucleus and behind other orbits.
const RADIUS_FRAC = 0.22;        // base radius of the smallest orbit, as a fraction of min(W,H)
const RADIUS_STEP = 0.07;        // each successive orbit grows by this fraction
const PATH_SAMPLES = 96;         // points per orbit when drawing the path
const PERSPECTIVE = 0.5;         // 0..1 — how strongly z affects size & alpha
const NUCLEUS_PULSE_SPEED = 0.0008; // rad/ms

// Each orbit is described by:
//   - eccentricity (0 = circle, ~0.5 = obvious ellipse)
//   - tiltX / tiltY: Euler rotations applied to its base XY-plane ellipse
//   - electronSpeed: angular velocity along the orbit (rad/ms)
//   - phase offset so electrons don't all start aligned
const ORBIT_DEFS = [
  { ecc: 0.10, tiltX: 0.95,  tiltY: 0.20,  electronSpeed: 0.0011,  phase: 0.0, electronCount: 1 },
  { ecc: 0.25, tiltX: -0.55, tiltY: 1.10,  electronSpeed: 0.00075, phase: 1.4, electronCount: 2 },
  { ecc: 0.05, tiltX: 0.30,  tiltY: -1.25, electronSpeed: 0.0014,  phase: 2.7, electronCount: 1 },
  { ecc: 0.18, tiltX: 1.35,  tiltY: 0.65,  electronSpeed: -0.0009, phase: 0.8, electronCount: 2 },
  { ecc: 0.32, tiltX: -1.05, tiltY: -0.45, electronSpeed: 0.00065, phase: 2.1, electronCount: 1 },
];

// Apply Euler rotation (X then Y) to a 3D point. The orbit's XY-plane
// ellipse is rotated into world space using these two angles.
function rotate(p, tiltX, tiltY) {
  // Rotate around X-axis.
  const cosX = Math.cos(tiltX);
  const sinX = Math.sin(tiltX);
  const y1 = p.y * cosX - p.z * sinX;
  const z1 = p.y * sinX + p.z * cosX;
  // Rotate around Y-axis.
  const cosY = Math.cos(tiltY);
  const sinY = Math.sin(tiltY);
  const x2 = p.x * cosY + z1 * sinY;
  const z2 = -p.x * sinY + z1 * cosY;
  return { x: x2, y: y1, z: z2 };
}

export default function AtomOrbits({ colors = [], style }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const colorsRef = useRef([DEFAULT_BG, DEFAULT_NUCLEUS, DEFAULT_ORBIT, DEFAULT_ELECTRON]);
  const orbitsRef = useRef([]);

  useEffect(() => {
    colorsRef.current = [
      pickColor(colors, 0, DEFAULT_BG),
      pickColor(colors, 1, DEFAULT_NUCLEUS),
      pickColor(colors, 2, DEFAULT_ORBIT),
      pickColor(colors, 3, DEFAULT_ELECTRON),
    ];
  }, [colors]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    let width = 0;
    let height = 0;
    const dpr = typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, 2) : 1;

    const seedOrbits = () => {
      // Pre-compute each orbit's path in world space (still 3D — per-frame
      // we'll project to 2D after applying a global system rotation).
      // This avoids re-running rotate() on PATH_SAMPLES points per orbit
      // every frame; we only have to re-rotate by the global angle.
      const minDim = Math.min(width, height);
      const baseR = minDim * RADIUS_FRAC;
      orbitsRef.current = ORBIT_DEFS.map((def, idx) => {
        const a = baseR + minDim * RADIUS_STEP * idx;     // semi-major
        const b = a * Math.sqrt(1 - def.ecc * def.ecc);   // semi-minor
        const samples = new Array(PATH_SAMPLES);
        for (let i = 0; i < PATH_SAMPLES; i += 1) {
          const theta = (i / PATH_SAMPLES) * Math.PI * 2;
          const p = { x: Math.cos(theta) * a, y: Math.sin(theta) * b, z: 0 };
          samples[i] = rotate(p, def.tiltX, def.tiltY);
        }
        return { def, a, b, samples };
      });
    };

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      // Use clientWidth/Height (layout dims) so an ancestor `transform: scale(...)`
      // (e.g. PreviewDeviceStage) doesn't shrink the canvas and pin it to top-left.
      width = Math.max(1, parent.clientWidth || parent.offsetWidth || 0);
      height = Math.max(1, parent.clientHeight || parent.offsetHeight || 0);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      seedOrbits();
    };

    const tick = (ts) => {
      const [bg, nucleusCol, orbitCol, electronCol] = colorsRef.current;
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, width, height);

      const cx = width / 2;
      const cy = height / 2;
      const orbits = orbitsRef.current;

      // A slow global rotation around the Y axis (camera spin) gives the
      // whole atom a 3D feel without recomputing per-orbit geometry.
      const globalY = ts * 0.00007;
      const cosG = Math.cos(globalY);
      const sinG = Math.sin(globalY);
      const project = (p) => {
        // Apply global Y rotation, then project. Z is in the same units
        // as X/Y (canvas px) so PERSPECTIVE just scales how much z biases
        // size/alpha — we don't apply real perspective division because
        // it can fold near-camera points to the other side of the screen.
        const x = p.x * cosG + p.z * sinG;
        const z = -p.x * sinG + p.z * cosG;
        const y = p.y;
        return { x: cx + x, y: cy + y, z };
      };

      // Build draw items (orbit path segments + electrons + nucleus) and
      // sort back-to-front by mean z so depth occlusion is correct.
      const items = [];

      // Orbit paths: for each orbit, project samples; emit one item per
      // segment so each segment can be sorted by its own mean depth and
      // pass behind / in front of the nucleus + electrons.
      for (let i = 0; i < orbits.length; i += 1) {
        const o = orbits[i];
        const projected = o.samples.map(project);
        for (let j = 0; j < projected.length; j += 1) {
          const a = projected[j];
          const b = projected[(j + 1) % projected.length];
          items.push({
            kind: "seg",
            z: (a.z + b.z) * 0.5,
            ax: a.x, ay: a.y,
            bx: b.x, by: b.y,
          });
        }
      }

      // Electrons: parameterize each electron along its orbit by current
      // angular position, build local point, rotate into orbit plane,
      // then project with global rotation.
      for (let i = 0; i < orbits.length; i += 1) {
        const o = orbits[i];
        const def = o.def;
        for (let k = 0; k < def.electronCount; k += 1) {
          const theta = ts * def.electronSpeed
            + def.phase
            + (k * Math.PI * 2) / def.electronCount;
          const local = { x: Math.cos(theta) * o.a, y: Math.sin(theta) * o.b, z: 0 };
          const world = rotate(local, def.tiltX, def.tiltY);
          const proj = project(world);
          items.push({
            kind: "electron",
            z: proj.z,
            x: proj.x, y: proj.y,
          });
        }
      }

      // Nucleus pulse — pulses gently in size with time. Always at z=0.
      items.push({
        kind: "nucleus",
        z: 0,
        x: cx, y: cy,
        scale: 1 + 0.08 * Math.sin(ts * NUCLEUS_PULSE_SPEED),
      });

      items.sort((a, b) => a.z - b.z);

      const minDim = Math.min(width, height);
      for (let i = 0; i < items.length; i += 1) {
        const it = items[i];

        if (it.kind === "seg") {
          // Depth in [0..1]: 0 = far/dim, 1 = near/bright.
          const depth = (it.z / minDim + 0.5);
          const clamped = Math.max(0, Math.min(1, depth));
          const alpha = 0.18 + clamped * 0.42;
          ctx.strokeStyle = rgba(orbitCol, alpha);
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(it.ax, it.ay);
          ctx.lineTo(it.bx, it.by);
          ctx.stroke();
        } else if (it.kind === "electron") {
          const depth = (it.z / minDim + 0.5);
          const clamped = Math.max(0, Math.min(1, depth));
          const sizeScale = 1 - PERSPECTIVE + PERSPECTIVE * clamped;
          const r = 4.5 * sizeScale;
          const alpha = 0.45 + clamped * 0.55;
          // Halo + core for the luminous electron look.
          const haloR = r * 4;
          const halo = ctx.createRadialGradient(it.x, it.y, 0, it.x, it.y, haloR);
          halo.addColorStop(0, rgba(electronCol, 0.55 * alpha));
          halo.addColorStop(1, rgba(electronCol, 0));
          ctx.fillStyle = halo;
          ctx.beginPath();
          ctx.arc(it.x, it.y, haloR, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = rgba(electronCol, alpha);
          ctx.beginPath();
          ctx.arc(it.x, it.y, r, 0, Math.PI * 2);
          ctx.fill();
        } else if (it.kind === "nucleus") {
          // Big radial halo + bright core. Always near the front-middle.
          const baseR = 9 * it.scale;
          const haloR = baseR * 4;
          const halo = ctx.createRadialGradient(it.x, it.y, 0, it.x, it.y, haloR);
          halo.addColorStop(0, rgba(nucleusCol, 0.85));
          halo.addColorStop(0.4, rgba(nucleusCol, 0.35));
          halo.addColorStop(1, rgba(nucleusCol, 0));
          ctx.fillStyle = halo;
          ctx.beginPath();
          ctx.arc(it.x, it.y, haloR, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = rgba(nucleusCol, 1);
          ctx.beginPath();
          ctx.arc(it.x, it.y, baseR, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    resize();
    rafRef.current = requestAnimationFrame(tick);

    let ro = null;
    if (typeof ResizeObserver !== "undefined" && canvas.parentElement) {
      ro = new ResizeObserver(resize);
      ro.observe(canvas.parentElement);
    } else if (typeof window !== "undefined") {
      window.addEventListener("resize", resize);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (ro) {
        try { ro.disconnect(); } catch { /* noop */ }
      } else if (typeof window !== "undefined") {
        window.removeEventListener("resize", resize);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        display: "block",
        pointerEvents: "none",
        ...style,
      }}
    />
  );
}
